import { colors } from 'fitify-ui'
import { ShareIcon } from 'fitify-ui/src/Icon/outline'
import { Typography } from 'fitify-ui-landing/src/components/Typography/Typography'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/exercises/Button/Button'
import {
  StyledBadge,
  StyledBadges,
} from '@/components/exercises/Exercise/Badge/Badge.Styled'
import {
  type BreadcrumbItem,
  Breadcrumbs,
} from '@/components/exercises/Exercise/Breadcrumbs/Breadcrumbs'
import {
  type ExerciseItem,
  getDifficultyI18nKey,
  getImpactI18nKey,
  getStanceI18nKey,
  getToolI18nKey,
} from '@/utils/exercises'

import {
  StyledContainer,
  StyledInfo,
  StyledThumbnail,
  StyledVideo,
  StyledVideoContainer,
  StyledWrapper,
} from './Hero.Styled'
import { PopularityCard } from './PopularityCard/PopularityCard'

type Props = {
  breadcrumbs: BreadcrumbItem[]
  exercise: ExerciseItem
}

export const Hero = ({ breadcrumbs, exercise }: Props) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current)
      }
    }
  }, [])

  const description = useMemo(() => {
    if (exercise.instructions?.hints) {
      return exercise.instructions.hints.map((hint) => t(hint)).join(' ')
    }

    return undefined
  }, [exercise.instructions?.hints, t])

  const handleShare = useCallback(() => {
    const share = async () => {
      const url = window.location.href
      const text = `${exercise.title} – ${t('landing_exercises_library_title')}`

      if (navigator.share) {
        try {
          await navigator.share({ title: text, url })
          return
        } catch {
          // User cancelled or share failed, fall through to clipboard
        }
      }

      try {
        await navigator.clipboard.writeText(url)
        if (copiedTimerRef.current) {
          clearTimeout(copiedTimerRef.current)
        }
        setCopied(true)
        copiedTimerRef.current = setTimeout(() => setCopied(false), 2000)
      } catch {
        // Silently fail if Clipboard API unavailable
      }
    }
    void share()
  }, [exercise.title, t])

  return (
    <StyledWrapper>
      <Breadcrumbs items={breadcrumbs} />
      <StyledContainer>
        <StyledVideoContainer>
          {exercise.video ? (
            <StyledVideo
              key={exercise.video}
              autoPlay
              muted
              loop
              playsInline
              poster={exercise.thumbnail}
            >
              <source src={exercise.video} type="video/mp4" />
            </StyledVideo>
          ) : exercise.thumbnail ? (
            <StyledThumbnail
              src={exercise.thumbnail}
              alt={exercise.title}
              loading="eager"
            />
          ) : null}
        </StyledVideoContainer>

        <StyledInfo>
          <Typography
            as="h1"
            $color={colors.Black}
            $variant="Headline/H 32 Bold"
            $variantPhone="Headline/H 28 Bold"
          >
            {exercise.title}
          </Typography>

          <PopularityCard exercise={exercise} />

          <StyledBadges>
            <StyledBadge $variant="blue">
              {t(getToolI18nKey(exercise.tool))}
            </StyledBadge>
            <StyledBadge $variant="green">
              {t(getDifficultyI18nKey(exercise.skill_required))}
            </StyledBadge>
            <StyledBadge>{t(getStanceI18nKey(exercise.stance))}</StyledBadge>
            {exercise.impact > 0 && (
              <StyledBadge>
                {t('landing_exercises_impact', {
                  level: t(getImpactI18nKey(exercise.impact)),
                })}
              </StyledBadge>
            )}
            {exercise.noisy <= 1 && (
              <StyledBadge $variant="green">
                {t('landing_exercises_apartment_friendly')}
              </StyledBadge>
            )}
          </StyledBadges>

          {description && (
            <Typography
              $color={colors.Gray600}
              $variant="Text/Body 16"
              $variantPhone="Text/Body 14"
            >
              {description}
            </Typography>
          )}
          <div>
            <Button icon={<ShareIcon />} onClick={handleShare}>
              {copied
                ? t('landing_exercises_link_copied')
                : t('landing_exercises_share')}
            </Button>
          </div>
        </StyledInfo>
      </StyledContainer>
    </StyledWrapper>
  )
}
